import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";

/**
 * Global one-time risk acknowledgement modal.
 * Shown once per browser (localStorage-persisted).
 * Users MUST tick both checkboxes to dismiss.
 */
export default function RiskAckModal() {
  const [open, setOpen] = useState(false);
  const [ackRisk, setAckRisk] = useState(false);
  const [ackAdult, setAckAdult] = useState(false);

  useEffect(() => {
    try {
      const acked = localStorage.getItem("qtr-risk-ack");
      if (!acked) setOpen(true);
    } catch { /* ignore */ }
  }, []);

  const accept = () => {
    try {
      localStorage.setItem("qtr-risk-ack", new Date().toISOString());
    } catch { /* ignore */ }
    window.dispatchEvent(new Event("anatradex-risk-acked"));
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="risk-ack-modal"
    >
      <div className="max-w-lg w-full rounded-2xl border-2 border-warn/50 bg-bg shadow-2xl">
        <div className="flex items-start gap-3 p-6 border-b border-white/10">
          <AlertTriangle className="text-warn shrink-0 mt-0.5" size={28} />
          <div className="flex-1">
            <h2 className="font-heading font-bold text-xl text-warn">Avant de commencer — le trading n&apos;est pas un jeu</h2>
            <p className="text-sm text-white/70 mt-2 leading-relaxed">
              Vous vous apprêtez à utiliser une plateforme d&apos;analyse de marchés financiers. Trader implique un
              <b> risque réel de perte, pouvant aller jusqu&apos;à la totalité de votre capital investi</b>. 70-85 %
              des traders retail perdent de l&apos;argent selon les régulateurs européens (ESMA).
            </p>
          </div>
        </div>
        <div className="p-6 space-y-3">
          <label className="flex items-start gap-3 cursor-pointer text-sm" data-testid="risk-ack-checkbox-1">
            <input
              type="checkbox"
              checked={ackRisk}
              onChange={(e) => setAckRisk(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-accent-blue"
            />
            <span className="text-white/80">
              Je reconnais avoir lu et compris la <Link to="/risk-disclosure" className="text-accent-blue underline" target="_blank">Déclaration de risques</Link>, les <Link to="/terms" className="text-accent-blue underline" target="_blank">CGU</Link> et la <Link to="/privacy" className="text-accent-blue underline" target="_blank">Politique de confidentialité</Link>. ANATRADEX est un outil éducatif et analytique, <b>pas un conseil en investissement</b>.
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer text-sm" data-testid="risk-ack-checkbox-2">
            <input
              type="checkbox"
              checked={ackAdult}
              onChange={(e) => setAckAdult(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-accent-blue"
            />
            <span className="text-white/80">
              J&apos;ai <b>au moins 18 ans</b>, je ne suis pas sous tutelle, et je m&apos;engage à ne trader que du capital que je peux me permettre de perdre intégralement.
            </span>
          </label>
        </div>
        <div className="p-6 border-t border-white/10 flex justify-end gap-3">
          <a
            href="/"
            className="btn text-sm px-4 py-2 text-white/60 hover:text-white"
            data-testid="risk-ack-decline-btn"
          >
            <X size={14} /> Non merci
          </a>
          <button
            onClick={accept}
            disabled={!ackRisk || !ackAdult}
            className="btn btn-primary text-sm px-6 py-2.5 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="risk-ack-accept-btn"
          >
            J&apos;accepte les risques et continue
          </button>
        </div>
      </div>
    </div>
  );
}
